import React, { createContext, useContext } from 'react'
import { useRecoilRefresher_UNSTABLE } from 'recoil'

import { $measurementUnit } from '../../../state/measurement-unit'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Cache = Record<string, Promise<any> | undefined>

export type AsyncCache = Omit<CacheState, 'data'>

export type FrameworkCaches = 'MeasurementUnit'

export interface CacheState {
  getOrCreate<T>(key: string, loadFn: () => Promise<T>): Promise<T>
  deleteEntry(key: string): void
  containsKey(key: string): boolean
  /**
   * delete all entries in cache for a given key prefix
   * @param prefix the prefix of the cache entries to remove, for example
   * if you wish to remove all patient entries the param would be `PATIENT`
   */
  deleteEntries(prefix: string): void
  invalidateFrameworkCache(cache: FrameworkCaches): void
}

export const INITIAL_STATE: CacheState = {
  getOrCreate: () => Promise.reject(),
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  deleteEntry: () => {},
  containsKey: () => false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  deleteEntries: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  invalidateFrameworkCache: (_: FrameworkCaches) => {},
}

export const MSDK_ASYNC_CACHE: Cache = {}

const cacheContext = createContext<CacheState>(INITIAL_STATE)
export const useAsyncCacheContext = () => useContext(cacheContext)

export class AsyncCacheHandler {
  /**
   * put here all framework recoil selectors that you whant to expose
   * to be invalidated from outside, to enable custom logic or actions
   * to invalidate the cache and cause a reload (Example use case: the measurement unit conversion
   * cache is loaded on application startup but the user can add or remove conversions using the
   * MEASUREMENT_UNIT entity, and the custom logic of that entity is invalidating the cache)
   */
  private invalidateMU = useRecoilRefresher_UNSTABLE($measurementUnit)

  private frameworkCacheAssociation: Record<FrameworkCaches, () => void> = {
    MeasurementUnit: this.invalidateMU,
  }

  public async getOrCreate<T>(key: string, loadFn: () => Promise<T>): Promise<T> {
    if (MSDK_ASYNC_CACHE[key]) return MSDK_ASYNC_CACHE[key]

    MSDK_ASYNC_CACHE[key] = loadFn()
    return MSDK_ASYNC_CACHE[key]
  }

  public deleteEntry(key: string): void {
    delete MSDK_ASYNC_CACHE[key]
  }

  public containsKey(key: string): boolean {
    return !!MSDK_ASYNC_CACHE[key]
  }

  public deleteEntries(prefix: string): void {
    const entriesToRemove = Object.keys(MSDK_ASYNC_CACHE).filter((entry) => entry.startsWith(`${prefix}.`))
    entriesToRemove.forEach((entry) => this.deleteEntry(entry))
  }

  public invalidateFrameworkCache(cache: FrameworkCaches) {
    const invalidate = this.frameworkCacheAssociation[cache]
    invalidate()
  }
}

export const AsyncCacheProvider = ({ children }: { children: React.ReactNode }) => {
  const cache = new AsyncCacheHandler()

  return <cacheContext.Provider value={cache}>{children}</cacheContext.Provider>
}
